import { io } from 'socket.io-client'
import axios from 'axios'
import { reactive } from 'vue'

class ChatService {
    constructor() {
        this.socket = null
        this.isConnected = false
        this.token = null
        this.baseURL = 'https://muzible.com/chatapi', //3004

        
        // Event listeners
        this.eventListeners = new Map()
        
        // Setup axios defaults
        axios.defaults.baseURL = this.baseURL
        this.userStatusesCache = reactive(new Map())
    }

    // Ustaw token i headers
    setToken(token) {
        this.token = token
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
    }

    async getUserStatusCache(userId) {
        if (this.userStatusesCache.has(userId)) {
            return this.userStatusesCache.get(userId)
        }
        try {
            const response = await axios.get(`/v1/users/${userId}/status`)
            const status = response.data
            this.userStatusesCache.set(userId, status.isOnline)
            return status.isOnline
        } catch (error) {
            return false
        }
    }

    setUserStatusCache(userId, isOnline) {
        this.userStatusesCache.set(userId, isOnline)
        this.emit('userStatusChanged', { userId, isOnline })
    }

    clearUserStatusCache() {
        this.userStatusesCache.clear()
    }

    // Połącz z socketem
    connect() {
        if (this.isConnected || !this.token || (this.socket && this.socket.connected)) return

        this.socket = io('https://muzible.com/', {
            path: '/chatapi/sockets/',
            auth: { token: this.token },
            transports: ['polling'], // Dodaj explicite
            upgrade: true,
            rememberUpgrade: true,
            forceNew: true,
            withCredentials: true, // Ważne dla CORS
            cors: {
                origin: window.location.origin,
                methods: ["GET", "POST", "PUT", "DELETE", "PATCH", "OPTIONS"],
                credentials: true
            }
        })

        // Connection events
        this.socket.on('connect', () => {
            this.isConnected = true
            this.emit('connected')
        })

        this.socket.on('disconnect', (reason) => {
            this.isConnected = false
            this.emit('disconnected', reason)
        })

        this.socket.on('connect_error', (error) => {
            this.emit('connectionError', error)
        })

        // Chat events
        this.socket.on('newMessage', (data) => this.emit('newMessage', data))
        this.socket.on('messagesSeen', (data) => this.emit('messagesSeen', data))
        this.socket.on('messageReactionUpdated', (data) => this.emit('messageReactionUpdated', data))
        this.socket.on('messageDeleted', (data) => this.emit('messageDeleted', data))
        
        // Chat management events
        this.socket.on('chatCreated', (data) => this.emit('chatCreated', data))
        this.socket.on('chatUpdated', (data) => this.emit('chatUpdated', data))
        this.socket.on('userAddedToChat', (data) => this.emit('userAddedToChat', data))
        this.socket.on('userRemovedFromChat', (data) => this.emit('userRemovedFromChat', data))
        this.socket.on('userLeftChat', (data) => this.emit('userLeftChat', data))
        this.socket.on('chatLeft', (data) => this.emit('chatLeft', data))
        this.socket.on('removedFromChat', (data) => this.emit('removedFromChat', data))
        this.socket.on('chatDeletedForUser', (data) => this.emit('chatDeletedForUser', data))
        this.socket.on('chatPermanentlyDeleted', (data) => this.emit('chatPermanentlyDeleted', data))
        this.socket.on('chatHidden', (data) => this.emit('chatHidden', data))
        this.socket.on('chatUnhidden', (data) => this.emit('chatUnhidden', data))
        this.socket.on('chatRestored', (data) => this.emit('chatRestored', data))
        this.socket.on('messageTooLong', (data) => this.emit('messageTooLong', data))
        this.socket.on('autoJoinedRoom', (data) => this.emit('autoJoinedRoom', data))

        this.socket.on('messageBlocked', (data) => this.emit('messageBlocked', data))
        this.socket.on('messageBlockedByPrivacy', (data) => this.emit('messageBlockedByPrivacy', data))

        // User status events
        this.socket.on('userOnline', (data) => this.emit('userOnline', data))
        this.socket.on('userOffline', (data) => this.emit('userOffline', data))
        
        // Typing events
        this.socket.on('userTyping', (data) => this.emit('userTyping', data))
        this.socket.on('userStoppedTyping', (data) => this.emit('userStoppedTyping', data))

        // Notification events
        this.socket.on('unreadNotificationsCount', (data) => this.emit('unreadNotificationsCount', data))

        // Server events
        this.socket.on('serverShutdown', (data) => this.emit('serverShutdown', data))
        this.socket.on('duplicateConnection', (data) => this.emit('duplicateConnection', data))
        this.socket.on('rateLimitExceeded', (data) => this.emit('rateLimitExceeded', data))

        // Error events
        this.socket.on('error', (data) => this.emit('error', data))
        this.socket.on('joinRoomError', (data) => this.emit('joinRoomError', data))
        this.socket.on('leaveRoomError', (data) => this.emit('leaveRoomError', data))

        // Keep alive
        setInterval(() => {
            if (this.isConnected) {
                this.socket.emit('pingUser')
            }
        }, 30000) // Co 30 sekund
    }

    // Rozłącz socket
    disconnect() {
        if (this.socket) {
            this.socket.disconnect()
            this.socket = null
            this.isConnected = false
        }
        this.userStatusesCache.clear()
    }

    // Event listener system
    on(event, callback) {
        if (!this.eventListeners.has(event)) {
            this.eventListeners.set(event, [])
        }
        this.eventListeners.get(event).push(callback)
    }

    off(event, callback) {
        if (this.eventListeners.has(event)) {
            const listeners = this.eventListeners.get(event)
            const index = listeners.indexOf(callback)
            if (index > -1) {
                listeners.splice(index, 1)
            }
        }
    }

    emit(event, data) {
        if (this.eventListeners.has(event)) {
            this.eventListeners.get(event).forEach(callback => {
                try {
                    callback(data)
                } catch (error) {
                    console.error(`Error in event listener for ${event}:`, error)
                }
            })
        }
    }

    // === SOCKET METHODS ===

    // Wyślij wiadomość
    sendMessage(chatId, content_usera) {
        if (!this.isConnected) throw new Error('Not connected to chat server')
        this.socket.emit('sendMessage', { chatId, content: content_usera.msg, replyContent: content_usera.replyContent, replyTo: content_usera.replyTo })
    }

    // Oznacz jako przeczytane
    markAsSeen(chatId) {
        if (!this.isConnected) return
        this.socket.emit('markAsSeen', { chatId })
    }

    // Typing indicators
    startTyping(chatId) {
        if (!this.isConnected) return
        this.socket.emit('typing', { chatId })
    }

    stopTyping(chatId) {
        if (!this.isConnected) return
        this.socket.emit('stopTyping', { chatId })
    }

    // React to message
    reactToMessage(messageId, reaction) {
        if (!this.isConnected) return
        this.socket.emit('reactToMessage', { messageId, reaction })
    }

    // Join/Leave rooms
    joinRoom(chatId) {
        if (!this.isConnected) return
        this.socket.emit('joinRoom', { chatId })
    }

    leaveRoom(chatId) {
        if (!this.isConnected) return
        this.socket.emit('leaveRoom', { chatId })
    }

    // === API METHODS ===

    // Pobierz wszystkie czaty
    // Pobierz wszystkie czaty
    async getChats(page = 1, limit = 10) {
        try {
            const response = await axios.get(`/v1/chats?page=${page}&limit=${limit}`);
            return response.data
        } catch (error) {
            console.error('Error fetching chats:', error)
        }
    }

    // Pobierz ukryte czaty
    async getHiddenChats(page = 1, limit = 10) {
        try {
            const response = await axios.get(`/v1/chats/hidden?page=${page}&limit=${limit}`);
            return response.data
        } catch (error) {
            console.error('Error fetching hidden chats:', error)
        }
    }

    // Stwórz nowy czat
    async createChat(recipients, groupName = null) {
        try {
            const response = await axios.post('/v1/chats/create', {
                recipients,
                groupName
            })
            return response.data
        } catch (error) {
            console.error('Error creating chat:', error)
        }
    }

    // Pobierz wiadomości z czatu
    async getMessages(chatId, page = 1, limit = 50) {
        try {
            const response = await axios.get(`/v1/chats/${chatId}/messages`, {
                params: { page, limit }
            })
            return response.data
        } catch (error) {
            console.error('Error fetching messages:', error)
        }
    }

    // Wyszukaj wiadomości
    async searchMessages(chatId, query) {
        try {
            const response = await axios.get(`/v1/chats/${chatId}/search`, {
                params: { q: query }
            })
            return response.data
        } catch (error) {
            console.error('Error searching messages:', error)
        }
    }

    // Zarządzanie użytkownikami w czacie
    async addUserToChat(chatId, userToAdd) {
        try {
            const response = await axios.post(`/v1/chats/${chatId}/add-user`, {
                userToAdd
            })
            return response.data
        } catch (error) {
            console.error('Error adding user to chat:', error)
        }
    }

    async removeUserFromChat(chatId, userToRemove) {
        try {
            const response = await axios.post(`/v1/chats/${chatId}/remove-user`, {
                userToRemove
            })
            return response.data
        } catch (error) {
            console.error('Error removing user from chat:', error)
        }
    }

    async leaveChat(chatId) {
        try {
            const response = await axios.post(`/v1/chats/${chatId}/leave`)
            return response.data
        } catch (error) {
            console.error('Error leaving chat:', error)
        }
    }

    // Zarządzanie czatem
    async deleteChat(chatId) {
        try {
            const response = await axios.post(`/v1/chats/${chatId}/delete`)
            return response.data
        } catch (error) {
            console.error('Error deleting chat:', error)
        }
    }

    async deleteGroupPermanently(chatId) {
        try {
            const response = await axios.delete(`/v1/chats/${chatId}/permanent-delete`)
            return response.data
        } catch (error) {
            console.error('Error permanently deleting chat:', error)
        }
    }

    async hideChat(chatId) {
        try {
            const response = await axios.post(`/v1/chats/${chatId}/hide`)
            return response.data
        } catch (error) {
            console.error('Error hiding chat:', error)
        }
    }

    async unhideChat(chatId) {
        try {
            const response = await axios.post(`/v1/chats/${chatId}/unhide`)
            return response.data
        } catch (error) {
            console.error('Error unhiding chat:', error)
        }
    }

    async updateGroupName(chatId, groupName) {
        try {
            const response = await axios.put(`/v1/chats/${chatId}/name`, {
                groupName
            })
            return response.data
        } catch (error) {
            console.error('Error updating group name:', error)
        }
    }

    async transferAdmin(chatId, newAdminId) {
        try {
            const response = await axios.post(`/v1/chats/${chatId}/transfer-admin`, {
                newAdminId
            })
            return response.data
        } catch (error) {
            console.error('Error transferring admin:', error)
        }
    }

    // Reakcje i usuwanie wiadomości
    async reactToMessageAPI(messageId, reaction) {
        try {
            const response = await axios.post(`/v1/messages/${messageId}/react`, {
                reaction
            })
            return response.data
        } catch (error) {
            console.error('Error reacting to message:', error)
        }
    }

    async deleteMessage(messageId) {
        try {
            const response = await axios.delete(`/v1/messages/${messageId}`)
            return response.data
        } catch (error) {
            console.error('Error deleting message:', error)
        }
    }

    // Blokowanie użytkowników
    async blockUser(userId) {
        try {
            const response = await axios.post(`/v1/users/${userId}/block`)
            return response.data
        } catch (error) {
            console.error('Error blocking user:', error)
        }
    }

    async unblockUser(userId) {
        try {
            const response = await axios.post(`/v1/users/${userId}/unblock`)
            return response.data
        } catch (error) {
            console.error('Error unblocking user:', error)
        }
    }

    // Ustawienia prywatności
    async updateMessagePermissions(allowMessagesFromAll) {
        try {
            const response = await axios.post('/v1/users/message-permissions', {
                allowMessagesFromAll
            })
            return response.data
        } catch (error) {
            console.error('Error updating message permissions:', error)
        }
    }

    async getPrivacySettings() {
        try {
            const response = await axios.get('/v1/users/privacy-settings')
            return response.data
        } catch (error) {
            console.error('Error fetching privacy settings:', error)
        }
    }

    async getUserPrivacySettings(userId) {
        try {
            const response = await axios.get(`/v1/users/${userId}/privacy`)
            return response.data
        } catch (error) {
            console.error('Error fetching user privacy settings:', error)
        }
    }

    // Status użytkowników
    async getUserStatus(userId) {
        try {
            const response = await axios.get(`/v1/users/${userId}/status`)
            return response.data
        } catch (error) {
            console.error('Error fetching user status:', error)
        }
    }

    async getBulkUserStatus(userIds) {
        try {
            const response = await axios.get('/v1/users/status', {
                params: { ids: userIds.join(',') }
            })
            return response.data
        } catch (error) {
            console.error('Error fetching bulk user status:', error)
        }
    }

    async usersSearch(query) {
        try {
            const response = await axios.get('/v1/users/search', {
                params: { q: query }
            })
            return response.data
        } catch (error) {
            console.error('Error searching users:', error)
        }
    }

    async getMyStatus() {
        try {
            const response = await axios.get('/v1/users/my-status')
            return response.data
        } catch (error) {
            console.error('Error fetching my status:', error)
        }
    }

    // Powiadomienia
    async getNotifications(page = 1, limit = 20) {
        try {
            const response = await axios.get('/v1/chats/notifications', {
                params: { page, limit }
            })
            return response.data
        } catch (error) {
            console.error('Error fetching notifications:', error)
        }
    }

    async getUnreadNotificationsCount() {
        try {
            const response = await axios.get('/v1/chats/notifications/unread-count')
            return response.data
        } catch (error) {
            console.error('Error fetching unread notifications count:', error)
        }
    }

    async markNotificationsAsRead(notificationIds = null) {
        try {
            const response = await axios.post('/v1/chats/notifications/mark-read', {
                notificationIds
            })
            return response.data
        } catch (error) {
            console.error('Error marking notifications as read:', error)
        }
    }

    async deleteAllNotifications() {
        try {
            const response = await axios.delete('/v1/chats/notifications/all')
            return response.data
        } catch (error) {
            console.error('Error deleting all notifications:', error)
        }
    }

    async deleteNotification(notificationId) {
        try {
            const response = await axios.delete(`/v1/chats/notifications/${notificationId}`)
            return response.data
        } catch (error) {
            console.error('Error deleting notification:', error)
        }
    }

    async clearAllChatHistory() {
        try {
            const response = await axios.delete('/v1/users/chat-history')
            return response.data
        } catch (error) {
            console.error('Error clearing all chat history:', error)
        }
    }

    gotoChat(notification) {
        this.emit('gotoChat', { chatId: notification.chatId })
    }

    gotoEmptyChat() {
        this.emit('gotoEmptyChat')
    }

    errorChat() {
        this.emit('rateLimitExceeded', {error: 'Search query must be at least 3 characters long.'})    
    }

}

// Singleton instance
export const chatService = new ChatService()
export default chatService